import { useOverlayPortalTarget } from "@/components/modal/overlay.hooks";
import { ZIndex } from "@/constants/z-index";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

// How far a press travels before it becomes a drag, so a click still clicks; the source file tabs
// use the same.
const DRAG_START_DISTANCE_PX = 4;
// How long a row takes to slide into its new place, as the machine type chain's rows do.
const SETTLE_DURATION_MS = 160;

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;

/**
 * A row's current vertical offset from its layout position, mid-transition included. Without
 * DOMMatrixReadOnly (jsdom), rows read as unmoved.
 */
const translateY = (row: HTMLElement) =>
  typeof DOMMatrixReadOnly === "undefined"
    ? 0
    : new DOMMatrixReadOnly(getComputedStyle(row).transform).m42;

/**
 * An element's layout top within `frame`, transforms aside: its offsets summed up to the frame.
 * When the frame is not among its offset parents (jsdom has none), its own offset stands in, as
 * for a row whose offset parent is the frame.
 */
const layoutTop = (element: HTMLElement, frame: HTMLElement | null) => {
  let top = 0;
  let node: Element | null = element;
  while (node instanceof HTMLElement && node !== frame) {
    top += node.offsetTop;
    node = node.offsetParent;
  }
  return node === frame ? top : element.offsetTop;
};

interface ActiveDrag {
  id: string;
  pointerId: number;
  /** The pointer's position within the frame's content when the drag began. */
  startY: number;
  /** The dragged row's layout top within the frame when the drag began. */
  startTop: number;
  pointerY: number;
}

export interface DragReorderList<T> {
  id: string;
  items: readonly T[];
  /** Whether rows from other lists may land here; false for a collapsed section. Defaults to true. */
  acceptsDrops?: boolean;
}

/** Every list's row ids, in list order. */
export type DragReorderArrangement = readonly { listId: string; ids: readonly string[] }[];

/**
 * Places outside the lists a row can be dropped on, such as a rail of icons. Over one, the lists
 * put the row back and show no slot, and a release there drops the row on it instead of
 * rearranging the lists.
 */
export interface DragReorderExternalTargets {
  /** The target at a viewport point, if the row `id` may land there. */
  targetAt: (clientX: number, clientY: number, id: string) => string | null;
  /** Follows the target under the pointer: null once the pointer leaves it or the drag ends. */
  onTargetChange?: (targetId: string | null) => void;
  /** As a drag starts and ends, so the targets can show they take the row meanwhile. */
  onDragChange?: (dragging: boolean) => void;
  onDropOnTarget: (id: string, targetId: string) => void;
}

const arrangementKey = (arrangement: DragReorderArrangement) =>
  arrangement.map(({ listId, ids }) => `${listId}\n${ids.join("\n")}`).join("\n\n");

const useCachedRefs = (elements: Map<string, HTMLElement>) => {
  const callbacksRef = useRef(new Map<string, (node: HTMLElement | null) => void>());
  return useCallback(
    (id: string) => {
      const existing = callbacksRef.current.get(id);
      if (existing != null) return existing;
      // Cached per id, so React keeps each element's ref attached across renders.
      const callback = (node: HTMLElement | null) => {
        if (node == null) elements.delete(id);
        else elements.set(id, node);
      };
      callbacksRef.current.set(id, callback);
      return callback;
    },
    [elements],
  );
};

/**
 * Reorders rows across one or more vertical lists by dragging, sorting as the pointer moves: the
 * dragged row follows the pointer, landing in the list whose rows the pointer is over or just
 * above, and the others slide aside into the arrangement a release would store. Escape, or losing
 * the pointer, puts everything back.
 *
 * It uses pointer events rather than native drag and drop, so the cursor can be the closed hand
 * for the whole gesture: a transparent layer over the page carries it, which also keeps hovers off.
 *
 * Positions are measured within one positioned frame element (`frameRef`) that holds every list.
 * Rows register with `registerRow(id)` and start drags with `onRowPointerDown`; with more than one
 * list, each list's element registers with `registerList(id)`, so the pointer can be matched to a
 * list. Elements between the lists that move with them, such as section titles, register with
 * `registerFollower(id)` to slide as the rows do. Rows may differ in height.
 */
export const useDragReorderLists = <T,>({
  lists,
  getId,
  onReorder,
  onDragStart,
  externalTargets,
}: {
  lists: readonly DragReorderList<T>[];
  getId: (item: T) => string;
  /** Every list's ids in their new arrangement, on release. Not called when nothing moved. */
  onReorder: (arrangement: DragReorderArrangement) => void;
  /** As the press becomes a drag, such as to close a hover panel. */
  onDragStart?: () => void;
  externalTargets?: DragReorderExternalTargets;
}) => {
  const frameRef = useRef<HTMLElement | null>(null);
  const rowsRef = useRef(new Map<string, HTMLElement>());
  const listElementsRef = useRef(new Map<string, HTMLElement>());
  const followersRef = useRef(new Map<string, HTMLElement>());
  const registerRow = useCachedRefs(rowsRef.current);
  const registerList = useCachedRefs(listElementsRef.current);
  const registerFollower = useCachedRefs(followersRef.current);

  // The arrangement on screen during a drag. The ref leads, since pointer moves can arrive faster
  // than React applies the state.
  const [dragArrangement, setDragArrangement] = useState<DragReorderArrangement | null>(null);
  const dragArrangementRef = useRef<DragReorderArrangement | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  // The dragged row's slot within the frame: where a release would land it.
  const [dropSlot, setDropSlot] = useState<{ top: number; height: number } | null>(null);
  // The target outside the lists the dragged row is over; its slot hides meanwhile.
  const [externalTargetId, setExternalTargetId] = useState<string | null>(null);
  const overExternalTargetRef = useRef(false);
  // The released row, while it slides from where the pointer left it into its place.
  const [settlingId, setSettlingId] = useState<string | null>(null);
  const endSettleRef = useRef<(() => void) | null>(null);
  const dragRef = useRef<ActiveDrag | null>(null);
  const suppressClickRef = useRef(false);
  const endGestureRef = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      endGestureRef.current?.();
      endSettleRef.current?.();
    },
    [],
  );

  const defaultArrangement = useMemo(
    (): DragReorderArrangement =>
      lists.map((list) => ({ listId: list.id, ids: list.items.map(getId) })),
    [getId, lists],
  );
  const itemsById = useMemo(
    () => new Map(lists.flatMap((list) => list.items.map((item) => [getId(item), item] as const))),
    [getId, lists],
  );
  const orderedLists = useMemo(
    (): ReadonlyMap<string, readonly T[]> =>
      new Map(
        dragArrangement == null
          ? lists.map((list) => [list.id, list.items])
          : dragArrangement.map(({ listId, ids }) => [
              listId,
              ids.flatMap((id) => {
                const item = itemsById.get(id);
                return item == null ? [] : [item];
              }),
            ]),
      ),
    [dragArrangement, itemsById, lists],
  );

  const topOf = (element: HTMLElement) => layoutTop(element, frameRef.current);
  const contentY = (clientY: number) => {
    const frame = frameRef.current;
    if (frame == null) return clientY;
    return clientY - frame.getBoundingClientRect().top - frame.clientTop + frame.scrollTop;
  };

  // Moves the dragged row to follow the pointer, kept within the rows' extent.
  const placeDraggedRow = useCallback(() => {
    const drag = dragRef.current;
    const row = drag == null ? undefined : rowsRef.current.get(drag.id);
    if (drag == null || row == null) return;
    const frame = frameRef.current;
    const rows = [...rowsRef.current.values()];
    const minTop = Math.min(...rows.map((candidate) => layoutTop(candidate, frame)));
    const maxTop = Math.max(
      ...rows.map((candidate) => layoutTop(candidate, frame) + candidate.offsetHeight),
    );
    const top = Math.min(
      Math.max(drag.startTop + drag.pointerY - drag.startY, minTop),
      maxTop - row.offsetHeight,
    );
    row.style.transition = "none";
    row.style.transform = `translateY(${top - layoutTop(row, frame)}px)`;
  }, []);

  // FLIP, during a drag and as a cancelled one puts the rows back: rows and followers that moved
  // paint where they were (mid-slide included), then slide to their new places. Places are layout
  // positions within the frame, snapshot as the drag starts and at each reorder, so the frame
  // moving as a whole, or rows resizing between drags, is no reorder. Snapshots are keyed by
  // element, so a row that remounts in another list starts fresh. The dragged row follows the
  // pointer instead.
  const tops = useRef(new Map<HTMLElement, number>());
  const animatesReorderRef = useRef(false);
  const snapshotTops = useCallback(() => {
    const frame = frameRef.current;
    const next = new Map<HTMLElement, number>();
    for (const element of [...rowsRef.current.values(), ...followersRef.current.values()]) {
      next.set(element, layoutTop(element, frame));
    }
    tops.current = next;
  }, []);
  // Lets the released row slide from where the pointer left it into its place. Held by id, since
  // a cancelled drag can remount the row in its own list.
  const settleRef = useRef<{ id: string; visualTop: number } | null>(null);
  const orderKey = arrangementKey(
    dragArrangement ?? lists.map((list) => ({ listId: list.id, ids: list.items.map(getId) })),
  );
  useLayoutEffect(() => {
    const previousTops = tops.current;
    snapshotTops();
    placeDraggedRow();
    const draggedRow =
      dragRef.current == null ? undefined : rowsRef.current.get(dragRef.current.id);
    if (draggedRow != null && !overExternalTargetRef.current) {
      setDropSlot({
        top: layoutTop(draggedRow, frameRef.current),
        height: draggedRow.offsetHeight,
      });
    }
    if (!animatesReorderRef.current) return;
    // A cancelled drag's revert is the last reorder this drag animates.
    if (dragRef.current == null) animatesReorderRef.current = false;
    if (prefersReducedMotion()) return;

    const frame = frameRef.current;
    const fixedRows = new Set(
      [dragRef.current?.id, settleRef.current?.id].flatMap((id) => {
        const row = id == null ? undefined : rowsRef.current.get(id);
        return row == null ? [] : [row];
      }),
    );
    const moved: { element: HTMLElement; delta: number }[] = [];
    for (const element of [...rowsRef.current.values(), ...followersRef.current.values()]) {
      const previousTop = previousTops.get(element);
      if (fixedRows.has(element) || previousTop == null) continue;
      const delta = previousTop + translateY(element) - layoutTop(element, frame);
      if (Math.abs(delta) >= 1) moved.push({ element, delta });
    }
    for (const { element, delta } of moved) {
      element.style.transition = "none";
      element.style.transform = `translateY(${delta}px)`;
    }
    // Not cancelled on a newer reorder: that one restarts each element from where it then is.
    window.requestAnimationFrame(() => {
      for (const { element } of moved) {
        element.style.transition = `transform ${SETTLE_DURATION_MS}ms ease-out`;
        element.style.transform = "";
      }
    });
  }, [orderKey, placeDraggedRow, snapshotTops]);

  // A release clears `draggingId` in the same update that leaves its row to settle.
  useLayoutEffect(() => {
    const settle = settleRef.current;
    if (settle == null || draggingId != null) return;
    settleRef.current = null;
    const row = rowsRef.current.get(settle.id);
    if (row == null) {
      setSettlingId(null);
      return;
    }
    const restingTop = layoutTop(row, frameRef.current);
    if (prefersReducedMotion() || Math.abs(settle.visualTop - restingTop) < 1) {
      row.style.transition = "";
      row.style.transform = "";
      setSettlingId(null);
      return;
    }
    row.style.transition = "none";
    row.style.transform = `translateY(${settle.visualTop - restingTop}px)`;
    window.requestAnimationFrame(() => {
      row.style.transition = `transform ${SETTLE_DURATION_MS}ms ease-out`;
      row.style.transform = "";
    });
    // Settled as the slide ends. Callers measure the rows then, so a timer, which can fire a frame
    // early, only covers a slide the browser never runs.
    let timer = 0;
    const settled = (event?: TransitionEvent) => {
      if (event != null && (event.target !== row || event.propertyName !== "transform")) return;
      row.removeEventListener("transitionend", settled);
      row.removeEventListener("transitioncancel", settled);
      window.clearTimeout(timer);
      endSettleRef.current = null;
      setSettlingId(null);
    };
    row.addEventListener("transitionend", settled);
    row.addEventListener("transitioncancel", settled);
    timer = window.setTimeout(settled, SETTLE_DURATION_MS * 2);
    endSettleRef.current = settled;
  }, [draggingId]);

  const onRowPointerDown = (event: React.PointerEvent<HTMLElement>, id: string) => {
    if (event.button !== 0 || !event.isPrimary || dragRef.current != null) return;
    const row = rowsRef.current.get(id);
    if (row == null) return;
    const pointerId = event.pointerId;
    const startClientX = event.clientX;
    const startClientY = event.clientY;
    const initialArrangement = defaultArrangement;
    const initialKey = arrangementKey(initialArrangement);
    const acceptsDrops = new Map(lists.map((list) => [list.id, list.acceptsDrops !== false]));
    let started = false;
    let targetId: string | null = null;
    const enterTarget = (nextTargetId: string | null) => {
      if (nextTargetId === targetId) return;
      targetId = nextTargetId;
      overExternalTargetRef.current = nextTargetId != null;
      setExternalTargetId(nextTargetId);
      externalTargets?.onTargetChange?.(nextTargetId);
    };
    const previousUserSelect = document.body.style.userSelect;

    const finish = (commit: boolean) => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleCancel);
      window.removeEventListener("keydown", handleKeyDown);
      endGestureRef.current = null;
      if (!started) return;

      const drag = dragRef.current;
      const arrangement = dragArrangementRef.current;
      const droppedOnTargetId = commit ? targetId : null;
      enterTarget(null);
      externalTargets?.onDragChange?.(false);
      const draggedRow = drag == null ? undefined : rowsRef.current.get(drag.id);
      if (draggedRow != null && drag != null) {
        settleRef.current = {
          id: drag.id,
          visualTop: layoutTop(draggedRow, frameRef.current) + translateY(draggedRow),
        };
        setSettlingId(drag.id);
      }
      // A committed arrangement is already on screen; only a cancelled one has rows to put back.
      animatesReorderRef.current = !commit;
      dragRef.current = null;
      dragArrangementRef.current = null;
      setDragArrangement(null);
      setDraggingId(null);
      setDropSlot(null);
      document.body.style.userSelect = previousUserSelect;
      if (droppedOnTargetId != null && drag != null) {
        externalTargets?.onDropOnTarget(drag.id, droppedOnTargetId);
      } else if (commit && arrangement != null && arrangementKey(arrangement) !== initialKey) {
        onReorder(arrangement);
      }
      // The press's own click, if the browser sends one, is part of the drag.
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    };
    const listBottom = (listId: string) => {
      const list = listElementsRef.current.get(listId) ?? frameRef.current;
      return list == null ? Infinity : topOf(list) + list.offsetHeight;
    };
    const handleMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      if (!started) {
        const travel = Math.hypot(
          moveEvent.clientX - startClientX,
          moveEvent.clientY - startClientY,
        );
        if (travel < DRAG_START_DISTANCE_PX) return;
        started = true;
        suppressClickRef.current = true;
        endSettleRef.current?.();
        snapshotTops();
        animatesReorderRef.current = true;
        dragRef.current = {
          id,
          pointerId,
          startY: contentY(startClientY),
          startTop: topOf(row),
          pointerY: contentY(moveEvent.clientY),
        };
        dragArrangementRef.current = initialArrangement;
        setDragArrangement(initialArrangement);
        setDraggingId(id);
        setDropSlot({ top: topOf(row), height: row.offsetHeight });
        document.body.style.userSelect = "none";
        onDragStart?.();
        externalTargets?.onDragChange?.(true);
      }
      moveEvent.preventDefault();

      const drag = dragRef.current;
      const arrangement = dragArrangementRef.current;
      if (drag == null || arrangement == null) return;
      drag.pointerY = contentY(moveEvent.clientY);
      const leftTarget = targetId != null;
      enterTarget(externalTargets?.targetAt(moveEvent.clientX, moveEvent.clientY, id) ?? null);
      if (targetId != null) {
        // Over a target outside the lists, the rows go back to where they were.
        if (arrangementKey(arrangement) !== initialKey) {
          dragArrangementRef.current = initialArrangement;
          setDragArrangement(initialArrangement);
        }
        setDropSlot(null);
        placeDraggedRow();
        return;
      }
      // Back over the lists, the slot shows again where the row now sits, remounted or not.
      const draggedRow = rowsRef.current.get(id) ?? row;
      if (leftTarget) setDropSlot({ top: topOf(draggedRow), height: draggedRow.offsetHeight });
      const source = arrangement.find((list) => list.ids.includes(id));
      if (source == null) return;
      // The pointer belongs to the first list whose bottom is below it, so the space above a list,
      // such as its title, leads into it; read from layout positions, as the slot is.
      const candidates = arrangement.filter(
        (list) => list.listId === source.listId || acceptsDrops.get(list.listId) === true,
      );
      const target =
        candidates.find((list) => drag.pointerY <= listBottom(list.listId)) ?? candidates.at(-1);
      if (target == null) return;
      // The slot is the count of the target's other rows whose middles the pointer is below, read
      // from layout positions: rows sliding past a still pointer would otherwise swap back and
      // forth.
      const slot = target.ids.filter((otherId) => {
        const other = otherId === id ? undefined : rowsRef.current.get(otherId);
        return other != null && topOf(other) + other.offsetHeight / 2 < drag.pointerY;
      }).length;
      if (target.listId !== source.listId || slot !== source.ids.indexOf(id)) {
        const next = arrangement.map((list) => {
          const ids = list.ids.filter((otherId) => otherId !== id);
          if (list.listId === target.listId) ids.splice(slot, 0, id);
          return ids.length === list.ids.length && list.listId !== target.listId
            ? list
            : { listId: list.listId, ids };
        });
        dragArrangementRef.current = next;
        setDragArrangement(next);
      }
      placeDraggedRow();
    };
    const handleUp = (upEvent: PointerEvent) => {
      if (upEvent.pointerId === pointerId) finish(true);
    };
    const handleCancel = (cancelEvent: PointerEvent) => {
      if (cancelEvent.pointerId === pointerId) finish(false);
    };
    const handleKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key !== "Escape" || !started) return;
      keyEvent.preventDefault();
      finish(false);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleCancel);
    window.addEventListener("keydown", handleKeyDown);
    endGestureRef.current = () => finish(false);
  };

  const onRowClickCapture = (event: React.MouseEvent) => {
    if (!suppressClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const portalTarget = useOverlayPortalTarget();
  const overlay =
    draggingId != null && portalTarget != null
      ? createPortal(
          <div
            aria-hidden={true}
            data-drag-reorder-overlay=""
            className={"fixed inset-0 cursor-grabbing"}
            style={{ zIndex: ZIndex.TOOLTIP }}
          />,
          portalTarget,
        )
      : null;

  return {
    frameRef,
    registerRow,
    registerList,
    registerFollower,
    /** Each list's items in the order on screen, by list id. */
    orderedLists,
    draggingId,
    /** The dragged row, or the released one until it settles into its place. */
    liftedId: draggingId ?? settlingId,
    /** The target outside the lists the dragged row is over, if any. */
    externalTargetId,
    /**
     * While dragging, the slot the dragged row would land in, within the frame's content, for a
     * drop indicator.
     */
    dropSlot,
    onRowPointerDown,
    onRowClickCapture,
    overlay,
  };
};

const SINGLE_LIST_ID = "list";

/**
 * Reorders one vertical list by dragging its rows (`useDragReorderLists` with a single list).
 *
 * The list element (`listRef`) must be positioned, so its rows' offsets are measured from it.
 * Every row registers with `registerRow(id)` and starts drags with `onRowPointerDown`; rows may
 * differ in height.
 */
export const useDragReorder = <T,>({
  items,
  getId,
  onReorder,
  onDragStart,
  externalTargets,
}: {
  items: readonly T[];
  getId: (item: T) => string;
  /** The new order of every item's id, on release. Not called when nothing moved. */
  onReorder: (ids: readonly string[]) => void;
  /** As the press becomes a drag, such as to close a hover panel. */
  onDragStart?: () => void;
  externalTargets?: DragReorderExternalTargets;
}) => {
  const lists = useMemo(() => [{ id: SINGLE_LIST_ID, items }], [items]);
  const reorder = useDragReorderLists({
    lists,
    getId,
    onReorder: (arrangement) => onReorder(arrangement[0]?.ids ?? []),
    onDragStart,
    externalTargets,
  });

  return {
    listRef: reorder.frameRef,
    registerRow: reorder.registerRow,
    orderedItems: reorder.orderedLists.get(SINGLE_LIST_ID) ?? items,
    draggingId: reorder.draggingId,
    liftedId: reorder.liftedId,
    externalTargetId: reorder.externalTargetId,
    dropSlot: reorder.dropSlot,
    onRowPointerDown: reorder.onRowPointerDown,
    onRowClickCapture: reorder.onRowClickCapture,
    overlay: reorder.overlay,
  };
};
